import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import './OrderInfo.css'
import { dpasRatingOptions, govPayTerms, productTypes, securityRequirementOptions, setAsides } from '../lists'
import { calculateGp, CurrencyInput, DatePicker, getRepCodeName } from '../helper'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DeleteOutline, ErrorOutlineOutlined, ExpandMore } from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'
import { confidenceType, lineItemType, orderType, repCodeType, shipToType } from '../types'
import { fieldLengths } from '../fieldLengths'
import { useDivisionList, useEndUserList, useInvoicingPlatformList, useRepCodes } from '../hooks'
import { getData } from 'country-list'

function OrderInfo({
  order,
  isCorrection,
  orderConf,
  confChanges,
  shippingAddresses,
  lineItems,
  originalQuote,
  setView,
  setOrder,
  setShippingAddresses,
  getContraqerLineItems,
  setContraqerBackup,
  lineItemsLoading,
  setLineItemsLoading,
}: {
  order: orderType
  isCorrection: boolean
  orderConf: React.MutableRefObject<confidenceType>
  confChanges: React.MutableRefObject<confidenceType>
  shippingAddresses: (shipToType & { default: boolean; id: number })[]
  lineItems: lineItemType[]
  originalQuote: string | null
  setOrder: React.Dispatch<React.SetStateAction<orderType>>
  setView: React.Dispatch<React.SetStateAction<number>>
  setLineItems: React.Dispatch<React.SetStateAction<lineItemType[]>>
  setShippingAddresses: React.Dispatch<React.SetStateAction<(shipToType & { default: boolean; id: number })[]>>
  setContraqerBackup: React.Dispatch<React.SetStateAction<Date | null>>
  getContraqerLineItems: () => void
  lineItemsLoading: 'loading' | 'error' | ''
  setLineItemsLoading: React.Dispatch<React.SetStateAction<'loading' | 'error' | ''>>
}) {
  const [repCodes, _setRepCodes] = useRepCodes()
  const invoicingPlatforms = useInvoicingPlatformList()
  const divisionList = useDivisionList()
  const endUserList = useEndUserList()

  const [licenseSameAsReceiver, setLicenseSameAsReceiver] = useState(false)
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(false)
  const [duplicateQuoteError, setDuplicateQuoteError] = useState(false)
  const [confirmed, setConfirmed] = useState({
    salesforceOpportunity: false,
    customerPO: false,
    totalCost: false,
    quoteExpires: order.PurchaseVehicle === 'ICPT UCOR',
    awardDate: false,
    grossMargin: false,
    setAside: false,
    productType: false,
    quoteNumber: false,
    orderTotal: false,
    dpasRating: order.PurchaseVehicle === 'ICPT UCOR',
    quoteDate: order.PurchaseVehicle === 'ICPT UCOR',
    shipToAttention: false,
    shipToCompany: false,
    shipToAddress1: false,
    shipToAddress2: false,
    shipToCity: false,
    shipToState: false,
    shipToZip: false,
    shipToCountry: false,
    billToAttention: false,
    billToCompany: false,
    billToAddress1: false,
    billToAddress2: order.PurchaseVehicle === 'ICPT UCOR',
    billToCity: order.PurchaseVehicle === 'ICPT UCOR',
    billToState: order.PurchaseVehicle === 'ICPT UCOR',
    billToZip: order.PurchaseVehicle === 'ICPT UCOR',
    billToCountry: order.PurchaseVehicle === 'ICPT UCOR',
    receiverContactName: false,
    receiverContactEmail: false,
    receiverContactPhone: false,
    deliveryInstructions: false,
    licenseContactName: false,
    licenseContactEmail: false,
    licenseContactPhone: false,
    shippingCharges: order.PurchaseVehicle === 'ICPT UCOR',
    govPaymentTerms: order.GovPaymentTerms === 'Credit Card',
    uidRequirement: order.PurchaseVehicle === 'ICPT UCOR',
    deliveryDueDate: false,
    vendorPaymentTerms: false,
    eplRequirement: order.PurchaseVehicle === 'ICPT UCOR',
    invoicingPlatform: order.PurchaseVehicle === 'ICPT UCOR',
    securityRequirement: order.PurchaseVehicle === 'ICPT UCOR',
    repCode: false,
    division: false,
    endUserType: false,
  })

  const countryList = useMemo(() => {
    return getData().sort((a, b) => (a.code === 'US' ? -1 : b.code === 'US' ? 1 : a.name.localeCompare(b.name)))
  }, [])

  const onContinue = () => {
    setLineItemsLoading('loading')
    Promise.all([
      axios.get('/contraqer/lastBackup'),
      lineItems.length === 0 || (originalQuote && originalQuote !== order.QuoteNumber)
        ? getContraqerLineItems()
        : Promise.resolve(),
    ])
      .then(([res, _]) => {
        setLineItemsLoading('')
        setContraqerBackup(new Date(res.data.ContraqerBackupTimestamp))
        setView(2)
      })
      .catch(err => {
        console.error(err)
        setLineItemsLoading('error')
      })
  }

  type inputRowProps<T> = {
    label: string
    value: T | null
    checked: boolean
    onCheckboxChange: (checked: boolean) => void
    onChange: (value: T | null) => void
    confidence?: number | null
    textWidth?: string
    inputOnly?: boolean
    disabled?: boolean
    required?: boolean
    noCheckbox?: boolean
    tooltip?: React.ReactNode
  }

  const InputWrapper = useMemo(
    () =>
      <T,>({
        children,
        label,
        value,
        checked,
        onCheckboxChange,
        confidence,
        textWidth = '50%',
        inputOnly = false,
        disabled = false,
        required = false,
        noCheckbox = false,
        tooltip,
      }: inputRowProps<T> & { children: React.ReactNode }) => {
        return (
          <div className="order-info-input-row">
            {!inputOnly && (
              <Typography variant="body2" sx={{ width: 'calc(100% - ' + textWidth + ')' }}>
                {label}
              </Typography>
            )}
            <div style={{ width: inputOnly ? '100%' : textWidth }}>
              {children}
              {tooltip}
              {confidence !== undefined && (
                <Typography>{confidence === null ? '-%' : `${(confidence * 100).toFixed(0)}%`}</Typography>
              )}
              {!noCheckbox && (
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onChange={e => {
                    const isEmpty = value === null || (typeof value === 'string' && value === '')
                    if (e.target.checked && required && isEmpty) {
                      return
                    }
                    onCheckboxChange(e.target.checked)
                  }}
                />
              )}
            </div>
          </div>
        )
      },
    [],
  )

  const InputRow = useMemo(
    () => (props: inputRowProps<string> & { maxLength?: number }) => {
      return (
        <InputWrapper {...props}>
          <TextField
            label={props.label}
            size="small"
            fullWidth
            value={props.value}
            onChange={e => props.onChange(e.target.value)}
            disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
            slotProps={props.maxLength ? { htmlInput: { maxLength: props.maxLength } } : undefined}
          />
        </InputWrapper>
      )
    },
    [],
  )

  const CurrencyInputRow = useMemo(
    () => (props: inputRowProps<number>) => {
      return (
        <InputWrapper {...props}>
          <CurrencyInput
            size="small"
            fullWidth
            value={props.value}
            onChange={value => props.onChange(value)}
            label={props.label}
            disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
          />
        </InputWrapper>
      )
    },
    [],
  )

  const SelectInputRow = useMemo(
    () => (props: inputRowProps<string> & { options: string[] }) => {
      return (
        <InputWrapper {...props}>
          <TextField
            select
            label={props.label}
            size="small"
            fullWidth
            value={props.value}
            onChange={e => props.onChange(e.target.value)}
            disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
          >
            {props.options.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </InputWrapper>
      )
    },
    [],
  )

  const AutocompleteInputRow = useMemo(
    () =>
      <T,>(props: inputRowProps<T> & { options: T[]; getOptionLabel?: (option: T) => string }) => {
        return (
          <InputWrapper {...props}>
            <Autocomplete
              fullWidth
              size="small"
              options={props.options}
              getOptionLabel={props.getOptionLabel}
              onChange={(_, value) => props.onChange(value)}
              value={props.value}
              renderInput={params => <TextField {...params} label={props.label} />}
              disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
            />
          </InputWrapper>
        )
      },
    [],
  )

  const DateInputRow = useMemo(
    () => (props: inputRowProps<Dayjs>) => {
      return (
        <InputWrapper {...props}>
          <DatePicker
            label={props.label}
            size="small"
            fullWidth
            value={props.value}
            onChange={value => props.onChange(value)}
            disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
          />
        </InputWrapper>
      )
    },
    [],
  )

  useEffect(() => {
    if (!isCorrection) {
      setOrder(prev => ({
        ...prev,
        VendorPaymentTerms: prev.VendorPaymentTerms ?? 'Net 30',
        GovPaymentTerms: prev.GovPaymentTerms ?? 'Net 30',
        DPASRating: prev.DPASRating ?? 'NONE',
        BillToAddressCountry: prev.BillToAddressCountry ?? 'United States of America (the)',
      }))
    }

    if (order.PurchaseVehicle === 'ICPT UCOR' && !isCorrection) {
      setOrder(prev => ({
        ...prev,
        GrossMargin: 0,
        ProductType: 'Hardware',
        BillToCompany: 'UCOR',
        BillToAddress1: 'invoices.ap@orcc.doe.gov',
        ShipToAttention: 'Chris Carpenter',
        ShipToAddress1: 'T3 UNITED CLEANUP OAK RIDGE LLC',
        ShipToAddress2: 'IT Deliveries 237 Brisbane Road T3',
        ShipToAddressCity: 'Oak Ridge',
        ShipToAddressZip: '37830',
        ShipToAddressState: 'TN',
        ShipToAddressCountry: 'United States',
        DeliveryDueDate: dayjs().add(30, 'day').toDate(),
        AwardDate: dayjs().toDate(),
        GovPaymentTerms: 'Net 15',
      }))

      if (shippingAddresses.length === 1) {
        setShippingAddresses(
          prev =>
            [
              {
                ShipToAttention: prev[0].ShipToAttention ?? 'Chris Carpenter',
                ShipToCompany: prev[0].ShipToCompany ?? 'UCOR',
                ShipToAddress1: prev[0].ShipToAddress1 ?? 'T3 UNITED CLEANUP OAK RIDGE LLC',
                ShipToAddress2: prev[0].ShipToAddress2 ?? 'IT Deliveries 237 Brisbane Road T3',
                ShipToAddressCity: prev[0].ShipToAddressCity ?? 'Oak Ridge',
                ShipToAddressZip: prev[0].ShipToAddressZip ?? '37830',
                ShipToAddressState: prev[0].ShipToAddressState ?? 'TN',
                ShipToAddressCountry: prev[0].ShipToAddressCountry ?? 'United States',
                ReceiverContactName: prev[0].ReceiverContactName ?? null,
                ReceiverContactEmail: prev[0].ReceiverContactEmail ?? null,
                ReceiverContactPhone: prev[0].ReceiverContactPhone ?? null,
                default: true,
                id: prev[0].id,
              },
            ] as (shipToType & { default: boolean; id: number })[],
        )
      }
    }
  }, [])

  useEffect(() => {
    if (order.PurchaseVehicle === 'ICPT UCOR') return

    setOrder(prev => ({
      ...prev,
      GrossMargin: calculateGp(
        prev.OrderTotal ?? 0,
        prev.TotalCost ?? 0,
        prev.PurchaseVehicle ?? '',
        prev.IsCreditCard || prev.GovPaymentTerms === 'Credit Card',
      ),
    }))
  }, [order.OrderTotal, order.TotalCost, order.PurchaseVehicle, order.IsCreditCard, order.GovPaymentTerms])

  return (
    <>
      <Alert severity="info" className="order-info-alert">
        Information must be confirmed using the checkboxes.
      </Alert>
      <div className="order-info-button-container">
        <Button variant="contained" onClick={() => setView(0)}>
          Previous
        </Button>
        <Button
          variant="contained"
          disabled={Object.values(confirmed).some(value => !value)}
          onClick={onContinue}
          loading={lineItemsLoading === 'loading'}
        >
          Next
        </Button>
      </div>
      <Accordion disableGutters defaultExpanded className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="24px">
            <Grid size={4}>
              <InputRow
                label="Salesforce Opportunity Name *"
                value={order.Opportunity}
                onChange={value => setOrder(prev => ({ ...prev, Opportunity: value }))}
                checked={confirmed.salesforceOpportunity}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, salesforceOpportunity: checked }))}
                inputOnly
                required
                maxLength={fieldLengths.orders.opportunity}
              />
            </Grid>
            <Grid size={4}>
              <CurrencyInputRow
                inputOnly
                required
                value={order.OrderTotal}
                onChange={value => {
                  setOrder(prev => ({ ...prev, OrderTotal: value }))
                  confChanges.current.OrderTotal = -1
                }}
                label="Order Total *"
                checked={confirmed.orderTotal}
                confidence={orderConf.current.OrderTotal}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, orderTotal: checked }))}
              />
            </Grid>
            <Grid size={4}>
              <InputRow
                label={order.PurchaseVehicle === 'ICPT UCOR' ? 'Reference # *' : 'IMPRES Quote # *'}
                value={order.QuoteNumber}
                onChange={value => {
                  setOrder(prev => ({ ...prev, QuoteNumber: value }))
                  confChanges.current.QuoteNumber = -1
                }}
                confidence={orderConf.current.QuoteNumber}
                checked={confirmed.quoteNumber}
                onCheckboxChange={checked => {
                  if (checked && !isCorrection) {
                    axios
                      .get(`/checkDuplicateOrder?quoteNumber=${order.QuoteNumber}`)
                      .then(response => {
                        if (response.data.exists) {
                          setDuplicateQuoteError(true)
                        } else {
                          setConfirmed(prev => ({ ...prev, quoteNumber: checked }))
                        }
                      })
                      .catch(error => {
                        console.error(error)
                      })
                  } else {
                    setConfirmed(prev => ({ ...prev, quoteNumber: checked }))
                  }
                }}
                inputOnly
                required
                maxLength={fieldLengths.orders.quoteNumber}
              />
            </Grid>
            <Grid size={4}>
              <InputRow
                label="Customer PO *"
                value={order.CustomerPO}
                onChange={value => {
                  setOrder(prev => ({ ...prev, CustomerPO: value }))
                  confChanges.current.CustomerPO = -1
                }}
                confidence={orderConf.current.CustomerPO}
                checked={confirmed.customerPO}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, customerPO: checked }))}
                inputOnly
                required
                maxLength={fieldLengths.orders.customerPO}
              />
            </Grid>
            <Grid size={4}>
              <CurrencyInputRow
                required
                inputOnly
                value={order.TotalCost}
                onChange={value => {
                  setOrder(prev => ({ ...prev, TotalCost: value }))
                  confChanges.current.TotalCost = -1
                }}
                label="Total Cost *"
                checked={confirmed.totalCost}
                confidence={orderConf.current.TotalCost}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, totalCost: checked }))}
              />
            </Grid>
            <Grid size={4}>
              <DateInputRow
                required
                inputOnly
                label="IMPRES Quote Date *"
                value={order.QuoteDate ? dayjs(order.QuoteDate) : null}
                onChange={value => {
                  setOrder(prev => ({ ...prev, QuoteDate: value ? value.toDate() : null }))
                  confChanges.current.QuoteDate = -1
                }}
                checked={confirmed.quoteDate}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, quoteDate: checked }))}
                confidence={orderConf.current.QuoteDate}
              />
            </Grid>
            <Grid size={4}>
              <InputWrapper
                required
                inputOnly
                label="Set Aside *"
                value={order.SetAside?.split(',') ?? []}
                onChange={() => {
                  return
                }}
                checked={confirmed.setAside}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, setAside: checked }))}
              >
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={order.SetAside?.split(',') ?? []}
                  label="Set Aside *"
                  slotProps={{
                    select: {
                      multiple: true,
                      onChange: e => setOrder(prev => ({ ...prev, SetAside: (e.target.value as string[]).join(',') })),
                    },
                  }}
                  disabled={confirmed.setAside}
                >
                  {setAsides.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </InputWrapper>
            </Grid>
            <Grid size={4}>
              <CurrencyInputRow
                required
                inputOnly
                value={order.GrossMargin}
                onChange={value => setOrder(prev => ({ ...prev, GrossMargin: value }))}
                label="Gross Margin *"
                checked={confirmed.grossMargin}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, grossMargin: checked }))}
                tooltip={
                  !!order.GrossMargin && order.GrossMargin < 0 ? (
                    <Tooltip
                      title="Gross Margin is negative. Please make sure this is intended and any approvals are uploaded."
                      placement="top"
                      slotProps={{ tooltip: { className: 'order-info-gross-margin-tooltip' } }}
                    >
                      <IconButton color="error">
                        <ErrorOutlineOutlined />
                      </IconButton>
                    </Tooltip>
                  ) : undefined
                }
              />
            </Grid>
            <Grid size={4}>
              <DateInputRow
                inputOnly
                required
                label="IMPRES Quote Expiration Date *"
                value={order.QuoteExpires ? dayjs(order.QuoteExpires) : null}
                onChange={value => {
                  setOrder(prev => ({ ...prev, QuoteExpires: value ? value.toDate() : null }))
                  confChanges.current.QuoteExpires = -1
                }}
                checked={confirmed.quoteExpires}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, quoteExpires: checked }))}
                confidence={orderConf.current.QuoteExpires}
              />
            </Grid>
            <Grid size={4}>
              <div className="order-info-input-row">
                <div style={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    required
                    select
                    size="small"
                    value={order.DPASRating ?? ''}
                    label="DPAS Rating *"
                    onChange={e => setOrder(prev => ({ ...prev, DPASRating: e.target.value }))}
                    disabled={confirmed.dpasRating}
                  >
                    {dpasRatingOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Checkbox
                    checked={confirmed.dpasRating}
                    onChange={e => {
                      if (!order.DPASRating && e.target.checked) {
                        return
                      }
                      setConfirmed(prev => ({ ...prev, dpasRating: e.target.checked }))
                    }}
                  />
                </div>
              </div>
            </Grid>
            <Grid size={4}>
              <SelectInputRow
                inputOnly
                required
                options={productTypes}
                label="Product Type *"
                value={order.ProductType ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, ProductType: value }))}
                checked={confirmed.productType}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, productType: checked }))}
              />
            </Grid>
            <Grid size={4}>
              <DateInputRow
                required
                inputOnly
                label="Award Date *"
                value={order.AwardDate ? dayjs(order.AwardDate) : null}
                onChange={value => {
                  setOrder(prev => ({ ...prev, AwardDate: value ? value.toDate() : null }))
                  confChanges.current.AwardDate = -1
                }}
                checked={confirmed.awardDate}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, awardDate: checked }))}
                confidence={orderConf.current.AwardDate}
              />
            </Grid>
            <Grid size={4}>
              <AutocompleteInputRow<repCodeType>
                required
                inputOnly
                options={repCodes.filter(code => code.IsActive).sort((a, b) => a.RepCode.localeCompare(b.RepCode))}
                label="Rep Code *"
                value={repCodes.find(code => code.RepCode === order.RepCode) ?? null}
                onChange={value => setOrder(prev => ({ ...prev, RepCode: value?.RepCode ?? '' }))}
                getOptionLabel={option => `${option.RepCode} ${getRepCodeName(option)}`}
                checked={confirmed.repCode}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, repCode: checked }))}
              />
            </Grid>
            <Grid size={4}>
              <SelectInputRow
                inputOnly
                required
                options={invoicingPlatforms}
                label="Invoicing Platform *"
                value={order.InvoicingPlatform ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, InvoicingPlatform: value }))}
                checked={confirmed.invoicingPlatform}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, invoicingPlatform: checked }))}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters defaultExpanded className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <div className="order-info-shipping-billing-header">
            <Typography variant="h6">Shipping and Billing Details</Typography>
            <FormControlLabel
              control={<Checkbox onClick={e => e.stopPropagation()} />}
              label="Billing information is the same as default shipping information"
              checked={billingSameAsShipping}
              onChange={(_e, checked) => {
                setBillingSameAsShipping(checked)
                if (checked) {
                  setOrder(prev => ({
                    ...prev,
                    BillToAttention: prev.ShipToAttention,
                    BillToCompany: prev.ShipToCompany,
                    BillToAddress1: prev.ShipToAddress1,
                    BillToAddress2: prev.ShipToAddress2,
                    BillToAddressCity: prev.ShipToAddressCity,
                    BillToAddressState: prev.ShipToAddressState,
                    BillToAddressZip: prev.ShipToAddressZip,
                    BillToAddressCountry: prev.ShipToAddressCountry,
                  }))
                  setConfirmed(prev => ({
                    ...prev,
                    billToAttention: prev.shipToAttention,
                    billToCompany: prev.shipToCompany,
                    billToAddress1: prev.shipToAddress1,
                    billToAddress2: prev.shipToAddress2,
                    billToCity: prev.shipToCity,
                    billToState: prev.shipToState,
                    billToZip: prev.shipToZip,
                    billToCountry: prev.shipToCountry,
                  }))
                }
              }}
              slotProps={{ typography: { onClick: e => e.stopPropagation() } }}
            />
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="56px">
            <Grid size={6}>
              <Stack spacing="17px">
                <div className="order-info-shipping-billing-content-header">
                  <Typography variant="h6">Shipping</Typography>
                </div>
                {shippingAddresses
                  .sort((a, b) => a.id - b.id)
                  .map((address, index) => (
                    <Accordion
                      defaultExpanded
                      disableGutters
                      className="order-info-shipping-address-accordion"
                      key={address.id}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Address {index + 1}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <FormControlLabel
                            control={<Radio />}
                            checked={address.default}
                            onChange={(_, checked) => {
                              setShippingAddresses(prev =>
                                prev.map(a => ({ ...a, default: a.id === address.id ? checked : false })),
                              )
                              setOrder(prev => ({
                                ...prev,
                                ShipToAttention: address.ShipToAttention,
                                ShipToCompany: address.ShipToCompany,
                                ShipToAddress1: address.ShipToAddress1,
                                ShipToAddress2: address.ShipToAddress2,
                                ShipToAddressCity: address.ShipToAddressCity,
                                ShipToAddressState: address.ShipToAddressState,
                                ShipToAddressZip: address.ShipToAddressZip,
                                ShipToAddressCountry: address.ShipToAddressCountry,
                              }))
                              setConfirmed(prev => ({
                                ...prev,
                                shipToAddress1: address.ShipToAddress1 ? prev.shipToAddress1 : false,
                              }))
                            }}
                            label="Set as default address"
                          />
                          <IconButton
                            onClick={() => setShippingAddresses(prev => prev.filter(a => a.id !== address.id))}
                            disabled={address.default}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </div>
                        <InputRow
                          label="Attention *"
                          value={address.ShipToAttention}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAttention: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAttention: value,
                                BillToAttention: billingSameAsShipping ? value : prev.BillToAttention,
                              }))
                            }
                          }}
                          checked={confirmed.shipToAttention}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToAttention: checked,
                              billToAttention: billingSameAsShipping ? checked : prev.billToAttention,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAttention}
                        />
                        <InputRow
                          label="Addressee (Company) *"
                          value={address.ShipToCompany}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToCompany: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToCompany: value,
                                BillToCompany: billingSameAsShipping ? value : prev.BillToCompany,
                              }))
                            }
                          }}
                          checked={confirmed.shipToCompany}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToCompany: checked,
                              billToCompany: billingSameAsShipping ? checked : prev.billToCompany,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToCompany}
                        />
                        <InputRow
                          label="Address Line 1 *"
                          value={address.ShipToAddress1}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddress1: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddress1: value,
                                BillToAddress1: billingSameAsShipping ? value : prev.BillToAddress1,
                              }))
                            }
                          }}
                          checked={confirmed.shipToAddress1}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToAddress1: checked,
                              billToAddress1: billingSameAsShipping ? checked : prev.billToAddress1,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddress1}
                        />
                        <InputRow
                          label="Address Line 2"
                          value={address.ShipToAddress2}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddress2: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddress2: value,
                                BillToAddress2: billingSameAsShipping ? value : prev.BillToAddress2,
                              }))
                            }
                          }}
                          checked={confirmed.shipToAddress2}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToAddress2: checked,
                              billToAddress2: billingSameAsShipping ? checked : prev.billToAddress2,
                            }))
                          }
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddress2}
                        />
                        <InputRow
                          label="City *"
                          value={address.ShipToAddressCity}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddressCity: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddressCity: value,
                                BillToAddressCity: billingSameAsShipping ? value : prev.BillToAddressCity,
                              }))
                            }
                          }}
                          checked={confirmed.shipToCity}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToCity: checked,
                              billToCity: billingSameAsShipping ? checked : prev.billToCity,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddressCity}
                        />
                        <InputRow
                          label="State *"
                          value={address.ShipToAddressState}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddressState: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddressState: value,
                                BillToAddressState: billingSameAsShipping ? value : prev.BillToAddressState,
                              }))
                            }
                          }}
                          checked={confirmed.shipToState}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToState: checked,
                              billToState: billingSameAsShipping ? checked : prev.billToState,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddressState}
                        />
                        <InputRow
                          label="Postal Code / Zip *"
                          value={address.ShipToAddressZip}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddressZip: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddressZip: value,
                                BillToAddressZip: billingSameAsShipping ? value : prev.BillToAddressZip,
                              }))
                            }
                          }}
                          checked={confirmed.shipToZip}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToZip: checked,
                              billToZip: billingSameAsShipping ? checked : prev.billToZip,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddressZip}
                        />
                        <AutocompleteInputRow<string>
                          options={countryList.map(({ name }) => name)}
                          label="Country *"
                          value={address.ShipToAddressCountry ?? ''}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddressCountry: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddressCountry: value,
                                BillToAddressCountry: billingSameAsShipping ? value : prev.BillToAddressCountry,
                              }))
                            }
                          }}
                          checked={confirmed.shipToCountry}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToCountry: checked,
                              billToCountry: billingSameAsShipping ? checked : prev.billToCountry,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                        />
                        <InputRow
                          label="Receiver Contact Name *"
                          value={address.ReceiverContactName}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ReceiverContactName: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ReceiverContactName: value,
                                LicenseContactName: licenseSameAsReceiver ? value : prev.LicenseContactName,
                              }))
                            }
                          }}
                          checked={confirmed.receiverContactName}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              receiverContactName: checked,
                              licenseContactName: licenseSameAsReceiver ? checked : prev.licenseContactName,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.receiverContactName}
                        />
                        <InputRow
                          label="Receiver Contact Email *"
                          value={address.ReceiverContactEmail}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ReceiverContactEmail: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ReceiverContactEmail: value,
                                LicenseContactEmail: licenseSameAsReceiver ? value : prev.LicenseContactEmail,
                              }))
                            }
                          }}
                          checked={confirmed.receiverContactEmail}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              receiverContactEmail: checked,
                              licenseContactEmail: licenseSameAsReceiver ? checked : prev.licenseContactEmail,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.receiverContactEmail}
                        />
                        <InputRow
                          label="Receiver Contact Phone *"
                          value={address.ReceiverContactPhone}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ReceiverContactPhone: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ReceiverContactPhone: value,
                                LicenseContactPhone: licenseSameAsReceiver ? value : prev.LicenseContactPhone,
                              }))
                            }
                          }}
                          checked={confirmed.receiverContactPhone}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              receiverContactPhone: checked,
                              licenseContactPhone: licenseSameAsReceiver ? checked : prev.licenseContactPhone,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.receiverContactPhone}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      setShippingAddresses(prev => [
                        ...prev,
                        {
                          id: prev.reduce((max, a) => Math.max(max, a.id), 0) + 1,
                          default: false,
                          ShipToAttention: null,
                          ShipToCompany: null,
                          ShipToAddress1: null,
                          ShipToAddress2: null,
                          ShipToAddressCity: null,
                          ShipToAddressState: null,
                          ShipToAddressZip: null,
                          ShipToAddressCountry: null,
                          ReceiverContactName: null,
                          ReceiverContactEmail: null,
                          ReceiverContactPhone: null,
                        },
                      ])
                    }
                    disabled={shippingAddresses.some(
                      a =>
                        !a.ShipToAttention ||
                        !a.ShipToCompany ||
                        !a.ShipToAddress1 ||
                        !a.ShipToAddressCity ||
                        !a.ShipToAddressState ||
                        !a.ShipToAddressZip ||
                        !a.ShipToAddressCountry ||
                        !a.ReceiverContactName ||
                        !a.ReceiverContactEmail ||
                        !a.ReceiverContactPhone,
                    )}
                  >
                    Add Address
                  </Button>
                </div>
                <FormControlLabel
                  control={<Checkbox />}
                  label="License POC same as Default Receiving POC"
                  checked={licenseSameAsReceiver}
                  onChange={(_e, checked) => {
                    setLicenseSameAsReceiver(checked)
                    if (checked) {
                      setOrder(prev => ({
                        ...prev,
                        LicenseContactName: prev.ReceiverContactName,
                        LicenseContactEmail: prev.ReceiverContactEmail,
                        LicenseContactPhone: prev.ReceiverContactPhone,
                      }))
                      setConfirmed(prev => ({
                        ...prev,
                        licenseContactName: prev.receiverContactName,
                        licenseContactEmail: prev.receiverContactEmail,
                        licenseContactPhone: prev.receiverContactPhone,
                      }))
                    }
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={6}>
              <Stack spacing="17px">
                <div className="order-details-shipping-billing-header">
                  <Typography variant="h6">Billing</Typography>
                </div>
                <InputRow
                  label="Attention"
                  value={order.BillToAttention ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAttention: value }))}
                  checked={confirmed.billToAttention}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToAttention: checked }))}
                  disabled={billingSameAsShipping}
                  maxLength={fieldLengths.orders.billToAttention}
                />
                <InputRow
                  label="Addressee (Company) *"
                  value={order.BillToCompany ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToCompany: value }))}
                  checked={confirmed.billToCompany}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToCompany: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToCompany}
                />
                <InputRow
                  label="Address Line 1 *"
                  value={order.BillToAddress1 ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddress1: value }))}
                  checked={confirmed.billToAddress1}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToAddress1: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToAddress1}
                />
                <InputRow
                  label="Address Line 2"
                  value={order.BillToAddress2 ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddress2: value }))}
                  checked={confirmed.billToAddress2}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToAddress2: checked }))}
                  disabled={billingSameAsShipping}
                  maxLength={fieldLengths.orders.billToAddress2}
                />
                <InputRow
                  label="City *"
                  value={order.BillToAddressCity ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddressCity: value }))}
                  checked={confirmed.billToCity}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToCity: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToAddressCity}
                />
                <InputRow
                  label="State *"
                  value={order.BillToAddressState ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddressState: value }))}
                  checked={confirmed.billToState}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToState: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToAddressState}
                />
                <InputRow
                  label="Postal Code / Zip *"
                  value={order.BillToAddressZip ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddressZip: value }))}
                  checked={confirmed.billToZip}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToZip: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToAddressZip}
                />
                <AutocompleteInputRow<string>
                  options={countryList.map(({ name }) => name)}
                  label="Country *"
                  value={order.BillToAddressCountry ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddressCountry: value }))}
                  checked={confirmed.billToCountry}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToCountry: checked }))}
                  disabled={billingSameAsShipping}
                  required
                />
              </Stack>
            </Grid>
          </Grid>
          <div className="order-info-delivery-instructions">
            <div>
              <Typography variant="body2">Delivery Instructions</Typography>
              <Checkbox
                checked={confirmed.deliveryInstructions}
                onChange={e => setConfirmed(prev => ({ ...prev, deliveryInstructions: e.target.checked }))}
              />
            </div>
            <TextField
              className="order-info-textarea"
              multiline
              label="Delivery Instructions"
              rows={3}
              fullWidth
              value={order.DeliveryInstructions ?? ''}
              onChange={e => setOrder(prev => ({ ...prev, DeliveryInstructions: e.target.value }))}
              disabled={confirmed.deliveryInstructions}
              slotProps={{ htmlInput: { maxLength: fieldLengths.orders.deliveryInstructions } }}
            />
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded disableGutters className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">License Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="56px" className="order-details-license-details-container">
            <Grid size={4}>
              <InputRow
                label="License Contact Name"
                value={order.LicenseContactName ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, LicenseContactName: value }))}
                textWidth="70%"
                checked={confirmed.licenseContactName}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, licenseContactName: checked }))}
                disabled={licenseSameAsReceiver}
                maxLength={fieldLengths.orders.licenseContactName}
              />
            </Grid>
            <Grid size={4}>
              <InputRow
                label="License Contact Email"
                value={order.LicenseContactEmail ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, LicenseContactEmail: value }))}
                textWidth="70%"
                checked={confirmed.licenseContactEmail}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, licenseContactEmail: checked }))}
                disabled={licenseSameAsReceiver}
                maxLength={fieldLengths.orders.licenseContactEmail}
              />
            </Grid>
            <Grid size={4}>
              <InputRow
                label="License Contact Phone"
                value={order.LicenseContactPhone ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, LicenseContactPhone: value }))}
                textWidth="70%"
                checked={confirmed.licenseContactPhone}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, licenseContactPhone: checked }))}
                disabled={licenseSameAsReceiver}
                maxLength={fieldLengths.orders.licenseContactPhone}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded disableGutters className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Netsuite Segments</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="56px">
            <Grid size={4}>
              <SelectInputRow
                label="Division"
                value={order.Division ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, Division: value }))}
                checked={confirmed.division}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, division: checked }))}
                textWidth="70%"
                options={divisionList}
              />
            </Grid>
            <Grid size={4}>
              <SelectInputRow
                label="End User Type"
                value={order.EndUserType ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, EndUserType: value }))}
                checked={confirmed.endUserType}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, endUserType: checked }))}
                textWidth="70%"
                options={endUserList}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded disableGutters className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Miscellaneous</Typography>
        </AccordionSummary>
        <AccordionDetails className="order-info-misc">
          <Grid container spacing="56px">
            <Grid size={4}>
              <Stack spacing="17px">
                <CurrencyInputRow
                  value={order.ShippingCharges}
                  onChange={value => {
                    setOrder(prev => ({ ...prev, ShippingCharges: value }))
                    confChanges.current.ShippingCharges = -1
                  }}
                  label="Shipping Charges"
                  checked={confirmed.shippingCharges}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, shippingCharges: checked }))}
                  confidence={orderConf.current.ShippingCharges}
                  textWidth="70%"
                />
                <DateInputRow
                  label="Delivery Due Date *"
                  value={order.DeliveryDueDate ? dayjs(order.DeliveryDueDate) : null}
                  onChange={value => {
                    setOrder(prev => ({ ...prev, DeliveryDueDate: value ? value.toDate() : null }))
                    confChanges.current.DeliveryDueDate = -1
                  }}
                  checked={confirmed.deliveryDueDate}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, deliveryDueDate: checked }))}
                  confidence={orderConf.current.DeliveryDueDate}
                  textWidth="70%"
                  required
                />
                <SelectInputRow
                  label="Security Requirement"
                  value={order.SecurityRequirement ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, SecurityRequirement: value }))}
                  checked={confirmed.securityRequirement}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, securityRequirement: checked }))}
                  confidence={orderConf.current.SecurityRequirement}
                  options={securityRequirementOptions}
                  textWidth="70%"
                />
              </Stack>
            </Grid>
            <Grid size={4}>
              <Stack spacing="17px">
                <SelectInputRow
                  label="Customer Payment Terms"
                  value={!!order.IsCreditCard ? 'Credit Card' : order.GovPaymentTerms ?? ''}
                  onChange={value => {
                    setOrder(prev => ({ ...prev, GovPaymentTerms: value, IsCreditCard: value === 'Credit Card' }))
                    if (value === 'Credit Card') {
                      setConfirmed(prev => ({ ...prev, govPaymentTerms: true }))
                    }
                  }}
                  checked={confirmed.govPaymentTerms}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, govPaymentTerms: checked }))}
                  confidence={orderConf.current.GovPaymentTerms}
                  options={govPayTerms}
                  textWidth="70%"
                  disabled={order.GovPaymentTerms === 'Credit Card'}
                  noCheckbox={order.GovPaymentTerms === 'Credit Card'}
                />
                <InputRow
                  label="Vendor Payment Terms"
                  value={order.VendorPaymentTerms}
                  onChange={value => {
                    setOrder(prev => ({ ...prev, VendorPaymentTerms: value }))
                    confChanges.current.VendorPaymentTerms = -1
                  }}
                  confidence={orderConf.current.VendorPaymentTerms}
                  textWidth="70%"
                  checked={confirmed.vendorPaymentTerms}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, vendorPaymentTerms: checked }))}
                  maxLength={fieldLengths.orders.vendorPaymentTerms}
                />
              </Stack>
            </Grid>
            <Grid size={4}>
              <Stack spacing="17px">
                <InputRow
                  label="UID Requirement"
                  value={order.UIDRequirement}
                  onChange={value => setOrder(prev => ({ ...prev, UIDRequirement: value }))}
                  textWidth="70%"
                  checked={confirmed.uidRequirement}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, uidRequirement: checked }))}
                  maxLength={fieldLengths.orders.uidRequirement}
                />
                <InputRow
                  label="EPL Requirement"
                  value={order.EPLRequirement}
                  onChange={value => setOrder(prev => ({ ...prev, EPLRequirement: value }))}
                  textWidth="70%"
                  checked={confirmed.eplRequirement}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, eplRequirement: checked }))}
                  maxLength={fieldLengths.orders.eplRequirement}
                />
              </Stack>
            </Grid>
          </Grid>
          <div className="order-info-special-instructions">
            <Typography variant="body2" sx={{ width: 'calc((100% - 112px) / 3 * .3)' }}>
              Special Order/PO Instructions
            </Typography>
            <TextField
              className="order-info-textarea"
              multiline
              label="Special Order/PO Instructions"
              rows={3}
              fullWidth
              sx={{ width: 'calc(100% - ((100% - 112px) / 3 * .3))' }}
              slotProps={{ htmlInput: { maxLength: fieldLengths.orders.specialInstructions } }}
              value={order.SpecialInstructions ?? ''}
              onChange={e => setOrder(prev => ({ ...prev, SpecialInstructions: e.target.value }))}
            />
          </div>
        </AccordionDetails>
      </Accordion>
      <div className="order-info-button-container">
        <Button variant="contained" onClick={() => setView(0)}>
          Previous
        </Button>
        <Button
          variant="contained"
          disabled={Object.values(confirmed).some(value => !value)}
          onClick={onContinue}
          loading={lineItemsLoading === 'loading'}
        >
          Next
        </Button>
      </div>
      <Snackbar open={duplicateQuoteError}>
        <Alert severity="error" variant="filled" onClose={() => setDuplicateQuoteError(false)}>
          <Typography>
            An order with this quote number already exists. Please enter a different quote number.
          </Typography>
        </Alert>
      </Snackbar>
      <Snackbar open={lineItemsLoading === 'error'}>
        <Alert severity="error" variant="filled" onClose={() => setLineItemsLoading('')}>
          An error occurred retrieving line items. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </>
  )
}

export default OrderInfo
